import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Eye, EyeOff, Loader2, ArrowLeft, Info, MailCheck } from "lucide-react";
import { XeroLogo } from "@/components/XeroLogo";
import { formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const shell = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" },
};

const SA_CELLPHONE_RE = /^(\+27|0)[6-8][0-9]{8}$/;

export default function Signup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    cellphone: "",
    password: "",
    confirm_password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { email }

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const inputBase =
    "w-full h-11 rounded-lg border border-slate-200 px-4 text-sm text-slate-800 placeholder:text-slate-400 outline-none transition-colors focus:border-[#13B5EA] focus:ring-2 focus:ring-[#13B5EA]/20";
  const primaryBtn =
    "w-full h-11 rounded-lg bg-[#13B5EA] text-white text-sm font-semibold shadow-sm transition-all hover:bg-[#0f9fce] active:scale-[0.99] disabled:opacity-60 flex items-center justify-center gap-2";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.password !== form.confirm_password) {
      setError("Passwords do not match");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    const cleanedCell = form.cellphone.replace(/[\s-]/g, "");
    if (!SA_CELLPHONE_RE.test(cleanedCell)) {
      setError("Please enter a valid South African cellphone number");
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/signup`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        cellphone: cleanedCell,
        password: form.password,
        confirm_password: form.confirm_password,
      });
      setSubmitted({ email: data.email });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#f4f8fb]" data-testid="signup-screen">
      <div className="w-full h-1.5 bg-[#13B5EA]" />

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <motion.div {...shell} className="w-full max-w-md">
          <div className="flex flex-col items-center text-center mb-8">
            <XeroLogo size={104} className="drop-shadow-sm" />
            <h1 className="mt-6 text-4xl font-bold tracking-tight text-[#1e2a32]">
              Xero <span className="text-[#13B5EA]">Payroll</span>
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              {submitted ? "Request submitted" : "Create your account"}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-[0_10px_40px_-12px_rgba(19,181,234,0.35)] border border-slate-100 p-8">
            {submitted ? (
              <div className="text-center space-y-4" data-testid="signup-success">
                <div className="mx-auto flex items-center justify-center w-14 h-14 rounded-full bg-[#e8f7fc]">
                  <MailCheck size={26} className="text-[#13B5EA]" />
                </div>
                <h2 className="text-lg font-semibold text-[#1e2d38]">Thanks for signing up!</h2>
                <p className="text-sm text-slate-500">
                  Your request for <span className="font-medium text-slate-700">{submitted.email}</span> has
                  been submitted and is awaiting administrator approval. You'll be able to sign in once
                  it's approved.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  data-testid="signup-back-to-login"
                  className={primaryBtn}
                >
                  Back to sign in
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="signup-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="first-name" className="block text-sm font-medium text-slate-700 mb-1.5">
                      First name
                    </label>
                    <input
                      id="first-name"
                      type="text"
                      required
                      value={form.first_name}
                      onChange={(e) => set("first_name", e.target.value)}
                      data-testid="signup-first-name-input"
                      className={inputBase}
                    />
                  </div>
                  <div>
                    <label htmlFor="last-name" className="block text-sm font-medium text-slate-700 mb-1.5">
                      Surname
                    </label>
                    <input
                      id="last-name"
                      type="text"
                      required
                      value={form.last_name}
                      onChange={(e) => set("last_name", e.target.value)}
                      data-testid="signup-last-name-input"
                      className={inputBase}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="signup-email" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Email address
                  </label>
                  <input
                    id="signup-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="you@company.co.za"
                    data-testid="signup-email-input"
                    className={inputBase}
                  />
                  <p className="mt-1.5 flex items-center gap-1.5 text-xs text-[#0f9fce]" data-testid="signup-email-tip">
                    <Info size={13} className="shrink-0" />
                    Your email address will serve as your username.
                  </p>
                </div>

                <div>
                  <label htmlFor="signup-cellphone" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Cellphone
                  </label>
                  <input
                    id="signup-cellphone"
                    type="tel"
                    required
                    value={form.cellphone}
                    onChange={(e) => set("cellphone", e.target.value)}
                    placeholder="082 123 4567"
                    data-testid="signup-cellphone-input"
                    className={inputBase}
                  />
                </div>

                <div>
                  <label htmlFor="signup-password" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="signup-password"
                      type={showPassword ? "text" : "password"}
                      required
                      value={form.password}
                      onChange={(e) => set("password", e.target.value)}
                      placeholder="At least 8 characters"
                      data-testid="signup-password-input"
                      className={`${inputBase} pr-11`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      data-testid="toggle-signup-password-visibility"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#13B5EA] transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label htmlFor="signup-confirm-password" className="block text-sm font-medium text-slate-700 mb-1.5">
                    Confirm password
                  </label>
                  <div className="relative">
                    <input
                      id="signup-confirm-password"
                      type={showConfirm ? "text" : "password"}
                      required
                      value={form.confirm_password}
                      onChange={(e) => set("confirm_password", e.target.value)}
                      placeholder="Re-enter your password"
                      data-testid="signup-confirm-password-input"
                      className={`${inputBase} pr-11`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((s) => !s)}
                      aria-label={showConfirm ? "Hide password" : "Show password"}
                      data-testid="toggle-signup-confirm-visibility"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#13B5EA] transition-colors"
                    >
                      {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div data-testid="signup-error" className="rounded-lg bg-red-50 border border-red-100 px-4 py-2.5 text-sm text-red-600">
                    {error}
                  </div>
                )}

                <button type="submit" disabled={loading} data-testid="signup-submit-button" className={primaryBtn}>
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  {loading ? "Submitting..." : "Sign up"}
                </button>

                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  data-testid="signup-back-to-login-link"
                  className="w-full flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-[#13B5EA] transition-colors"
                >
                  <ArrowLeft size={14} /> Back to sign in
                </button>
              </form>
            )}
          </div>

          <p className="mt-8 text-center text-xs text-slate-400">
            © {new Date().getFullYear()} Xero Payroll. All rights reserved.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
